import React, { useEffect, useRef, useState } from 'react';
import { GOLD, GOLD_LT, CREAM } from '../theme';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const pad2 = (n) => String(n).padStart(2, '0');
const toISO = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;

function parseISO(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return { y, m: m - 1, d };
}

export default function DatePicker({ value, onChange, inputId }) {
  const parsed = parseISO(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [open, setOpen] = useState(false);
  const [viewY, setViewY] = useState(parsed ? parsed.y : today.getFullYear());
  const [viewM, setViewM] = useState(parsed ? parsed.m : today.getMonth());
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const firstWeekday = new Date(viewY, viewM, 1).getDay();
  const daysInMonth = new Date(viewY, viewM + 1, 0).getDate();
  const cells = [...Array(firstWeekday).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const changeMonth = (delta) => {
    let m = viewM + delta;
    let y = viewY;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setViewM(m);
    setViewY(y);
  };

  const selectDay = (d) => {
    onChange(toISO(viewY, viewM, d));
    setOpen(false);
  };

  const display = parsed ? `${MONTHS[parsed.m]} ${parsed.d}, ${parsed.y}` : '';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        id={inputId}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{ width: '100%', textAlign: 'left', background: 'rgba(201,162,75,.05)', border: 'none', borderBottom: '1px solid rgba(201,162,75,.35)', color: display ? CREAM : 'rgba(239,228,210,.45)', fontFamily: "'Cormorant Garamond', serif", fontSize: 18, padding: '14px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <span>{display || 'Select a date'}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.6"><rect x="3" y="5" width="18" height="16" rx="1.5" /><path d="M8 3v4M16 3v4M3 10h18" /></svg>
      </button>

      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 40, background: '#1c1109', border: '1px solid rgba(201,162,75,.35)', boxShadow: '0 12px 40px rgba(0,0,0,.5)', padding: 18, width: 280 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <button type="button" onClick={() => changeMonth(-1)} aria-label="Previous month" style={{ background: 'none', border: 'none', color: GOLD_LT, cursor: 'pointer', fontSize: 18, padding: 4 }}>‹</button>
            <span style={{ fontFamily: "'Bodoni Moda', serif", fontSize: 16, color: CREAM }}>{MONTHS[viewM]} {viewY}</span>
            <button type="button" onClick={() => changeMonth(1)} aria-label="Next month" style={{ background: 'none', border: 'none', color: GOLD_LT, cursor: 'pointer', fontSize: 18, padding: 4 }}>›</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4, marginBottom: 6 }}>
            {WEEKDAYS.map((w, i) => (
              <div key={i} style={{ textAlign: 'center', fontSize: 11, letterSpacing: '.1em', color: 'rgba(239,228,210,.45)' }}>{w}</div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4 }}>
            {cells.map((d, i) => {
              if (d === null) return <div key={i} />;
              const isPast = new Date(viewY, viewM, d) < today;
              const isSelected = parsed && parsed.y === viewY && parsed.m === viewM && parsed.d === d;
              return (
                <button
                  key={i}
                  type="button"
                  className="ij-day-btn"
                  disabled={isPast}
                  onClick={() => selectDay(d)}
                  style={{
                    aspectRatio: '1 / 1',
                    background: isSelected ? GOLD : 'transparent',
                    color: isPast ? 'rgba(239,228,210,.25)' : isSelected ? '#180b0a' : CREAM,
                    border: 'none',
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 14,
                    cursor: isPast ? 'default' : 'pointer',
                  }}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
